import { useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";

import { AssignmentChipSection, type AssignmentItem } from "../components/AssignmentChipSection";

interface RoleSummary {
  id: string;
  name: string;
  description?: string;
}

export interface UserRolesSectionProps {
  userId: string;
  /** The role catalog, hoisted to the parent so it loads alongside the user rather than after it. */
  catalog: RoleSummary[] | null;
  catalogError?: boolean;
}

// Role assignment for a user — writes the SPA-login RBAC table (fapi_user_account_role) via
// PUT /api/users/{id}/roles, which is exactly what resolves the user's JWT authorities.
//
// The endpoint takes a FULL SET, but the screen no longer asks the admin to assemble one: each chip
// add/remove sends the currently-known set plus or minus that one role. The set is only ever derived
// from a LOADED read (AssignmentChipSection renders nothing until `assigned` is known), so an
// unloaded or failed read can no longer be turned into an accidental "remove everything".
export function UserRolesSection({ userId, catalog, catalogError = false }: UserRolesSectionProps) {
  const t = useTranslate();
  const {
    data: assignedData,
    refetch,
    isError: assignedError,
  } = useCustom<RoleSummary[]>({ url: `users/${userId}/roles`, method: "get" });
  const { mutate: save, isLoading: isSaving } = useCustomMutation();

  const [error, setError] = useState<string | null>(null);

  const assignedRoles = Array.isArray(assignedData?.data) ? (assignedData!.data as RoleSummary[]) : null;
  const assigned: AssignmentItem<string>[] | null =
    assignedRoles?.map((r) => ({ id: r.id, label: r.name })) ?? null;

  // Only roles the user does not already hold — the dialog never offers a duplicate.
  const held = new Set(assignedRoles?.map((r) => r.id) ?? []);
  const options: AssignmentItem<string>[] | null =
    catalog === null || assignedRoles === null
      ? null
      : catalog.filter((r) => !held.has(r.id)).map((r) => ({ id: r.id, label: r.name }));

  function write(roleIds: string[]) {
    setError(null);
    save(
      { url: `users/${userId}/roles`, method: "put", values: { roleIds } },
      {
        onSuccess: () => void refetch(),
        // A silent failure here is worse than most: the admin walks away believing the authorities
        // changed when they did not.
        onError: () => setError(t("userDetailView.roles.saveFailed")),
      },
    );
  }

  return (
    <AssignmentChipSection
      className="user-roles"
      title={t("userDetailView.roles.title")}
      assigned={assigned}
      options={options}
      isError={catalogError || assignedError}
      busy={isSaving}
      actionError={error}
      onAdd={(roleId) => write([...held, roleId])}
      onRemove={(roleId) => write([...held].filter((id) => id !== roleId))}
      labels={{
        loading: t("common.loading"),
        loadFailed: t("userDetailView.roles.loadFailed"),
        empty: t("userDetailView.roles.none"),
        add: t("userDetailView.roles.add"),
        addDialogTitle: t("userDetailView.roles.addTitle"),
        addDialogEmpty: t("userDetailView.roles.addNoneLeft"),
        removeTitle: t("userDetailView.roles.removeTitle"),
        removeBody: (label) => t("userDetailView.roles.removeBody").replace("%s", label),
        removeConfirm: t("common.remove"),
        cancel: t("common.cancel"),
        removeAria: (label) => t("userDetailView.roles.removeAria").replace("%s", label),
        searchPlaceholder: t("userDetailView.roles.searchPlaceholder"),
        searchNoMatches: t("common.noMatches"),
      }}
    />
  );
}
